<script setup lang="ts">
import { onMounted, reactive, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { verifyAccountDetails } from '@/api/lytAccountingClient'
import {
  findPackageById,
  usePackageMetadataQuery,
} from '@/composables/queries/usePackageMetadataQuery'
import { getIntegration } from '@/api/integrations/index.js'
import {
  ArrowRight,
  KeyRound,
  Landmark,
  Link2,
  LockKeyhole,
  ShieldCheck,
} from 'lucide-vue-next'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { Alert, Badge, Button, Card, Input } from '@/components/ui'
import { track } from '@/lib/usageTracker'
import { setupFlowSteps } from './setupFlow'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const packageId = computed(() => route.params.packageId?.toString() || '')
const invoiceNumber = computed(() => route.query.invoiceNumber?.toString() || '')
// Accept both storeID and storeId query spellings used by intake links.
const storeID = computed(
  () => (route.query.storeID ?? route.query.storeId)?.toString() || '',
)

// Same package-metadata cache entry as PackageSelection (invoice + store only).
const {
  data: packagesData,
  isLoading: packagesLoading,
  error: packagesError,
} = usePackageMetadataQuery({
  invoiceNumber,
  storeId: storeID,
})

const selectedPackage = computed(() =>
  findPackageById(packagesData.value, packageId.value),
)

const actionError = ref('')
const requirements = ref<{
  packageId: string
  authType: string
  fields: Array<{
    name: string
    label?: string
    type?: string
    required?: boolean
    options?: Array<{ value: string; label: string }>
    help?: string
  }>
} | null>(null)
const creds = reactive<Record<string, string>>({})

const integration = computed(() => getIntegration(packageId.value))
const providerName = computed(() => integration.value?.name ?? packageId.value ?? 'Provider')
const providerColor = computed(() => integration.value?.iconColor ?? '#2ca01c')

// Keep prior loading-card behavior while metadata is in flight.
const loading = computed(() => packagesLoading.value)

// Prefer write/action errors; otherwise map metadata read failures / not-found.
const error = computed(() => {
  if (actionError.value) return actionError.value
  if (packagesError.value) return 'Failed to load package information.'
  if (!packagesLoading.value && packagesData.value && !selectedPackage.value) {
    return 'Package not found.'
  }
  return ''
})

const contextItems = computed(() => [
  { label: 'Invoice reference', value: invoiceNumber.value || 'Pending verification' },
  { label: 'Store reference', value: storeID.value || 'Pending verification' },
  { label: 'Provider', value: providerName.value || 'Pending package selection', tone: 'accent' },
  {
    label: 'Connection method',
    value:
      requirements.value?.authType === 'OAUTH'
        ? 'OAuth handoff'
        : requirements.value?.authType === 'API_KEY'
          ? 'Credential verification'
          : 'Loading requirements',
    tone: requirements.value ? 'default' : 'accent',
  },
])

const providerBenefits = computed(() => {
  if (requirements.value?.authType === 'OAUTH') {
    return [
      'Approve access directly with the provider and return to LYT Accounting with your setup context preserved.',
      'The provider manages sign-in; LYT Accounting only receives the scopes needed to sync store records.',
      'You can reconnect later from the dashboard if access ever expires.',
    ]
  }

  return [
    'Verify company credentials against the provider before enabling sync actions in the workspace.',
    'Keep account, store, and invoice provenance visible while entering the required details.',
    'Credential checks happen inside the current setup flow so you can retry without losing state.',
  ]
})

function initCreds(fields: Array<{ name: string }>) {
  fields.forEach((f) => {
    if (f && f.name) creds[f.name] = ''
  })
}

function mapFriendlyError(message = '') {
  const msg = String(message)
  if (/403/i.test(msg) && /eligible/i.test(msg)) return 'This invoice is not eligible for this accounting add‑on yet.'
  if (/403/i.test(msg)) return 'You do not have permission to perform this action.'
  if (/401/i.test(msg)) return 'Please sign in again to continue.'
  if (/400/i.test(msg)) return 'Some details are missing or invalid. Please review and try again.'
  if (/5\d\d/.test(msg)) return 'Our servers are having trouble right now. Please try again in a moment.'
  return msg || 'Something went wrong.'
}

/**
 * Derive login requirements from the cached package row (case-insensitive id match).
 * Leaves verification/OAuth writes untouched — only the metadata read moved to Colada.
 */
function applyPackageRequirements(pkg: NonNullable<typeof selectedPackage.value>) {
  const authType = (
    (pkg as { loginType?: string | null; authType?: string }).loginType
    || (pkg as { authType?: string }).authType
    || 'API_KEY'
  ).toUpperCase()

  if (authType.startsWith('OAUTH')) {
    requirements.value = { packageId: packageId.value, authType: 'OAUTH', fields: [] }
    return
  }

  const fields = [
    { name: 'companyId', label: 'Company ID', type: 'text', required: true },
    { name: 'apiKey', label: 'API Key', type: 'password', required: true },
  ]
  requirements.value = { packageId: packageId.value, authType: 'API_KEY', fields }
  initCreds(fields)
}

watch(
  selectedPackage,
  (pkg) => {
    if (!pkg) {
      requirements.value = null
      return
    }
    applyPackageRequirements(pkg)
  },
  { immediate: true },
)

function isValid() {
  if (!requirements.value) return false
  const authType = (requirements.value.authType || '').toUpperCase()
  if (authType.startsWith('OAUTH')) return true
  const fields = requirements.value.fields || []
  return fields.every(
    (f) => !f.required || (creds[f.name] && String(creds[f.name]).trim().length > 0),
  )
}

async function submit() {
  track('form:ProviderLogin', 'submit', { provider: packageId.value })
  if (!isValid()) return
  actionError.value = ''
  try {
    auth.setCredentials({ ...creds })
    auth.setVerificationStatus('loading')
    const payload = {
      invoiceNumber: auth.invoiceNumber,
      storeId: auth.storeID,
      credentials: { ...creds },
    }
    const resp = (await verifyAccountDetails(packageId.value, payload)) as {
      verified?: boolean
      account?: Record<string, unknown>
    }
    if (resp && resp.verified) {
      auth.setVerificationStatus('success', { account: resp.account })
      const isOauth = (requirements.value?.authType || '').toUpperCase().startsWith('OAUTH')
      if (integration.value && isOauth) {
        auth.setQueryParams({ invoiceNumber: auth.invoiceNumber, storeID: auth.storeID })
        const url = integration.value.startOAuth(auth.storeID, auth.invoiceNumber)
        window.location.assign(url)
        return
      }
      router.push({ name: 'SetupSuccess', query: route.query })
    } else {
      auth.setVerificationStatus('error', { error: 'Verification failed.' })
      actionError.value = 'Verification failed. Please check your details and try again.'
    }
  } catch (e) {
    console.error(e)
    auth.setVerificationStatus('error', { error: (e as Error)?.message || 'Verification failed.' })
    actionError.value = mapFriendlyError((e as Error)?.message || '')
  }
}

function goOAuth() {
  track('btn:ConnectOAuth', 'click')
  submit()
}

onMounted(() => {
  auth.setQueryParams({ invoiceNumber: invoiceNumber.value, storeID: storeID.value })

  if (packageId.value.toLowerCase() === 'sap') {
    router.replace({ name: 'PackageSelection', query: route.query })
  }
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Connection handoff"
    :title="`Connect ${providerName}`"
    subtitle="Review provider requirements, confirm your setup references, and complete the connection without leaving the intake flow."
    :current-step="3"
    :steps="setupFlowSteps"
    :provider-name="providerName"
    :provider-color="providerColor"
    :context-items="contextItems"
  >
    <Card v-if="loading" padding="lg" class="login-loading">
      <div class="login-loading__title">Loading provider requirements</div>
      <p class="login-loading__copy">Fetching the connection method and required fields for {{ providerName }}.</p>
    </Card>

    <div v-else-if="requirements" class="login-grid">
      <Card padding="lg" class="spotlight-panel">
        <div class="spotlight-panel__header">
          <div class="spotlight-panel__mark" :style="{ background: providerColor }">
            <Landmark class="h-5 w-5" />
          </div>
          <div>
            <div class="spotlight-panel__eyebrow">Provider spotlight</div>
            <h2 class="spotlight-panel__title">{{ providerName }}</h2>
          </div>
        </div>

        <div class="spotlight-panel__badge-row">
          <Badge>{{ requirements.authType === 'OAUTH' ? 'OAuth handoff' : 'Credential verification' }}</Badge>
          <Badge size="sm" variant="success">Store-linked setup</Badge>
        </div>

        <p class="spotlight-panel__copy">
          {{ requirements.authType === 'OAUTH'
            ? `You'll leave this page briefly to approve ${providerName}, then return with the same invoice and store context.`
            : `Enter the account details required by ${providerName} so LYT Accounting can verify the connection before sync actions are enabled.` }}
        </p>

        <div class="spotlight-benefits">
          <div v-for="benefit in providerBenefits" :key="benefit" class="spotlight-benefits__item">
            <ShieldCheck class="h-4 w-4" />
            <span>{{ benefit }}</span>
          </div>
        </div>
      </Card>

      <Card padding="lg" class="connection-panel">
        <div class="connection-panel__header">
          <div>
            <div class="connection-panel__eyebrow">Connection details</div>
            <h2 class="connection-panel__title">
              {{ requirements.authType === 'OAUTH' ? 'Approve provider access' : 'Verify account details' }}
            </h2>
          </div>
          <div class="connection-panel__context">
            <span>Store {{ storeID || '—' }}</span>
            <span>Invoice {{ invoiceNumber || '—' }}</span>
          </div>
        </div>

        <template v-if="requirements.authType === 'OAUTH'">
          <div class="oauth-card">
            <div class="oauth-card__icon">
              <Link2 class="h-5 w-5" />
            </div>
            <div>
              <div class="oauth-card__title">Redirect to {{ providerName }}</div>
              <p class="oauth-card__copy">Continue to the provider, approve access, and return here to finish setup.</p>
            </div>
          </div>

          <Button class="connection-panel__primary" @click="goOAuth">
            Connect with {{ providerName }}
            <ArrowRight class="h-4 w-4" />
          </Button>
        </template>

        <template v-else>
          <form class="credentials-form" @submit.prevent="submit">
            <div class="credentials-form__field" v-for="f in requirements.fields" :key="f.name">
              <Input
                v-if="!f.type || f.type === 'text' || f.type === 'password'"
                :id="f.name"
                v-model="creds[f.name]"
                :type="f.type === 'password' ? 'password' : 'text'"
                :label="f.label || f.name"
                :required="!!f.required"
                :placeholder="f.label || f.name"
              />

              <div v-else-if="f.type === 'select'" class="select-field">
                <label :for="f.name">{{ f.label || f.name }}</label>
                <select :id="f.name" v-model="creds[f.name]">
                  <option disabled value="">Please select</option>
                  <option v-for="opt in (f.options || [])" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>

              <small v-if="f.help" class="credentials-form__help">{{ f.help }}</small>
            </div>

            <Button type="submit" class="connection-panel__primary" :disabled="!isValid()">
              <KeyRound class="h-4 w-4" />
              Verify and continue
            </Button>
          </form>
        </template>

        <Alert v-if="error" variant="error" title="We couldn't continue">
          {{ error }}
          <p class="connection-panel__support">If this persists, contact <a href="mailto:support@chrilantech.com">support@chrilantech.com</a>.</p>
        </Alert>

        <div class="connection-panel__trust">
          <LockKeyhole class="h-4 w-4" />
          <span>{{ requirements.authType === 'OAUTH' ? 'Approval is completed with the provider.' : 'Credentials are checked before the workspace is unlocked.' }}</span>
        </div>
      </Card>
    </div>

    <Alert v-else variant="error" title="Provider requirements unavailable">
      {{ error || 'We could not load the package requirements for this provider.' }}
    </Alert>
  </SetupFlowShell>
</template>

<style scoped>
.login-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 1rem;
  align-items: start;
}

.login-loading {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.35rem;
}

.login-loading__title,
.spotlight-panel__title,
.connection-panel__title,
.oauth-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--color-ink);
}

.login-loading__copy,
.spotlight-panel__copy,
.oauth-card__copy,
.connection-panel__support,
.credentials-form__help {
  margin: 0.35rem 0 0;
  color: var(--color-ink-soft);
  font-size: 0.88rem;
}

.spotlight-panel,
.connection-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.spotlight-panel__header,
.connection-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.spotlight-panel__header {
  justify-content: flex-start;
}

.spotlight-panel__mark {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-md);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.spotlight-panel__eyebrow,
.connection-panel__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.spotlight-panel__badge-row,
.connection-panel__context {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.connection-panel__context {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-ink-soft);
}

.spotlight-benefits {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.spotlight-benefits__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.7rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-soft);
  background: rgba(255, 255, 255, 0.76);
  color: var(--color-ink-soft);
  font-size: 0.84rem;
}

.oauth-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  border-radius: var(--radius-sm);
  border: 1px solid rgba(79, 70, 229, 0.16);
  background: rgba(79, 70, 229, 0.04);
  padding: 0.9rem;
}

.oauth-card__icon {
  width: 2.2rem;
  height: 2.2rem;
  border-radius: var(--radius-sm);
  background: rgba(79, 70, 229, 0.1);
  color: var(--color-primary-dark);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.credentials-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.credentials-form__field,
.select-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.select-field label {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--color-ink);
}

.select-field select {
  height: 2.5rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: white;
  padding: 0 0.75rem;
  font-size: 0.9rem;
  color: var(--color-ink);
}

.connection-panel__primary {
  width: 100%;
  justify-content: center;
}

.connection-panel__trust {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.8rem;
  color: var(--color-ink-soft);
  border-top: 1px solid var(--color-border-soft);
  padding-top: 0.9rem;
}

@media (max-width: 880px) {
  .login-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .login-grid {
    gap: 0.7rem;
  }

  .connection-panel {
    order: 1;
    gap: 0.85rem;
  }

  .spotlight-panel {
    order: 2;
    gap: 0.85rem;
  }

  .connection-panel__header {
    flex-direction: column;
    gap: 0.55rem;
  }

  .connection-panel__context {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.45rem;
  }

  .connection-panel__context span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 2rem;
    padding: 0.35rem 0.5rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border-soft);
    background: var(--color-surface-muted);
    text-align: center;
  }

  .credentials-form {
    gap: 0.75rem;
  }

  .oauth-card {
    padding: 0.8rem;
  }

  .spotlight-panel__badge-row {
    gap: 0.35rem;
  }

  .spotlight-benefits {
    gap: 0.45rem;
  }

  .spotlight-benefits__item {
    align-items: flex-start;
    padding: 0.55rem 0.6rem;
    font-size: 0.8rem;
  }

  .connection-panel__trust {
    align-items: flex-start;
  }

  .connection-panel__primary :deep(button) {
    width: 100%;
  }

  .select-field select {
    width: 100%;
  }
}
</style>
